import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './lib/auth.js';
import { Layout } from './components/Layout.js';
import { Login } from './pages/Login.js';
import { Overview } from './pages/Overview.js';
import { Credentials } from './pages/Credentials.js';
import { Settings } from './pages/Settings.js';
import { ExportEditor } from './pages/ExportEditor.js';
import { SheetsHub } from './pages/SheetsHub.js';
import { PpcOverview } from './pages/PpcOverview.js';
import { PpcActions } from './pages/PpcActions.js';
import { PpcSettings } from './pages/PpcSettings.js';
import { InventoryOverview } from './pages/InventoryOverview.js';
import { InventoryStranded } from './pages/InventoryStranded.js';
import { InventoryStorage } from './pages/InventoryStorage.js';
import { InventoryBatches } from './pages/InventoryBatches.js';
import { InventoryRemovals } from './pages/InventoryRemovals.js';
import { Products } from './pages/Products.js';
import { FinanceOverview } from './pages/FinanceOverview.js';
import { FinanceSKU } from './pages/FinanceSKU.js';
import { FinanceCosts } from './pages/FinanceCosts.js';
import { FinanceSettlements } from './pages/FinanceSettlements.js';
import { FinanceReimbursements } from './pages/FinanceReimbursements.js';
// FinancePnlReport merged into FinanceSettlements
import { FinanceAccounting } from './pages/FinanceAccounting.js';
import { MessagesInbox } from './pages/MessagesInbox.js';
import { ReviewRequests } from './pages/ReviewRequests.js';
import { ProductReviews } from './pages/ProductReviews.js';
import { KeepaTracking } from './pages/KeepaTracking.js';
import { NicheExplorer } from './pages/NicheExplorer.js';
import { ListingOptimizer } from './pages/ListingOptimizer.js';
import { CompetitiveIntel } from './pages/CompetitiveIntel.js';
import { KeywordRanking } from './pages/KeywordRanking.js';
import { SqpOverview } from './pages/SqpOverview.js';
import { SalesRegional } from './pages/SalesRegional.js';
import { SalesOrders } from './pages/SalesOrders.js';
import { SalesReturns } from './pages/SalesReturns.js';
import { SalesTrends } from './pages/SalesTrends.js';
import { SalesMovingAvg } from './pages/SalesMovingAvg.js';
import { SalesDayparting } from './pages/SalesDayparting.js';
import { SalesRefundRate } from './pages/SalesRefundRate.js';
import { RoiSimulator } from './pages/RoiSimulator.js';
import { AdminTables } from './pages/AdminTables.js';
import { SheetsSync } from './pages/SheetsSync.js';
import { FinanceCashflow } from './pages/FinanceCashflow.js';
import { FinanceHealth } from './pages/FinanceHealth.js';
import { PpcAutomation } from './pages/PpcAutomation.js';
import { PpcChangeLog } from './pages/PpcChangeLog.js';
import { PpcCampaigns } from './pages/PpcCampaigns.js';
import { PpcTrends } from './pages/PpcTrends.js';
import { SplitTests } from './pages/SplitTests.js';
import { SystemHealth } from './pages/SystemHealth.js';
import { FinanceTax } from './pages/FinanceTax.js';

export function App() {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/accounts" element={<Navigate to="/credentials" replace />} />
        <Route path="/credentials" element={<Credentials />} />
        <Route path="/settings" element={<Settings />} />
        {/* Sheets Hub (overview, exports, history, connections) */}
        <Route path="/sheets" element={<SheetsHub />} />
        <Route path="/exports/new" element={<ExportEditor />} />
        <Route path="/exports/:id/edit" element={<ExportEditor />} />
        {/* Redirects from old Data Sync routes */}
        <Route path="/exports" element={<Navigate to="/sheets?tab=exports" replace />} />
        <Route path="/sheets/connections" element={<Navigate to="/sheets?tab=connections" replace />} />
        <Route path="/history" element={<Navigate to="/sheets?tab=history" replace />} />
        {/* Sales Hub */}
        <Route path="/sales" element={<SalesRegional />} />
        <Route path="/sales/trends" element={<SalesTrends />} />
        <Route path="/sales/moving-avg" element={<SalesMovingAvg />} />
        <Route path="/sales/dayparting" element={<SalesDayparting />} />
        <Route path="/sales/refund-rate" element={<SalesRefundRate />} />
        <Route path="/sales/roi-simulator" element={<RoiSimulator />} />
        <Route path="/sales/orders" element={<SalesOrders />} />
        <Route path="/sales/returns" element={<SalesReturns />} />
        {/* Module B: PPC Intelligence */}
        <Route path="/ppc" element={<PpcOverview />} />
        <Route path="/ppc/actions" element={<PpcActions />} />
        <Route path="/ppc/settings" element={<PpcSettings />} />
        <Route path="/ppc/campaigns" element={<PpcCampaigns />} />
        <Route path="/ppc/automation" element={<PpcAutomation />} />
        <Route path="/ppc/trends" element={<PpcTrends />} />
        <Route path="/ppc/log" element={<PpcChangeLog />} />
        {/* Module D: Inventory Intelligence */}
        <Route path="/inventory" element={<InventoryOverview />} />
        <Route path="/inventory/stranded" element={<InventoryStranded />} />
        <Route path="/inventory/storage" element={<InventoryStorage />} />
        <Route path="/inventory/batches" element={<InventoryBatches />} />
        <Route path="/inventory/removals" element={<InventoryRemovals />} />
        <Route path="/inventory/products" element={<Products />} />
        <Route path="/inventory/listing-optimizer" element={<ListingOptimizer />} />
        {/* Communication Hub */}
        <Route path="/messages" element={<MessagesInbox />} />
        <Route path="/messages/reviews" element={<ReviewRequests />} />
        <Route path="/messages/product-reviews" element={<ProductReviews />} />
        {/* Module E: Competitive Radar */}
        <Route path="/competitive" element={<CompetitiveIntel />} />
        <Route path="/competitive/keywords" element={<KeywordRanking />} />
        <Route path="/competitive/tracking" element={<KeepaTracking />} />
        <Route path="/competitive/niche-explorer" element={<NicheExplorer />} />
        <Route path="/competitive/sqp" element={<SqpOverview />} />
        <Route path="/competitive/split-tests" element={<SplitTests />} />
        {/* Admin */}
        <Route path="/system-health" element={<SystemHealth />} />
        <Route path="/sheets-sync" element={<SheetsSync />} />
        <Route path="/admin" element={<AdminTables />} />
        <Route path="/admin/tables/:name" element={<AdminTables />} />
        <Route path="/admin/query" element={<Navigate to="/admin" replace />} />
        {/* Module C: Financial Brain */}
        <Route path="/finance" element={<FinanceOverview />} />
        <Route path="/finance/skus" element={<FinanceSKU />} />
        <Route path="/finance/costs" element={<FinanceCosts />} />
        <Route path="/finance/settlements" element={<FinanceSettlements />} />
        <Route path="/finance/reimbursements" element={<FinanceReimbursements />} />
        <Route path="/finance/pnl" element={<Navigate to="/finance/settlements" replace />} />
        <Route path="/finance/accounting" element={<FinanceAccounting />} />
        <Route path="/finance/tax" element={<FinanceTax />} />
        <Route path="/finance/cashflow" element={<FinanceCashflow />} />
        <Route path="/finance/health" element={<FinanceHealth />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
